import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui';
import { pasosAplicables, type Paso } from './pasos';

/**
 * Tutorial de bienvenida: oscurece la pantalla, deja a la vista una zona y
 * explica qué es.
 *
 *  · Se puede omitir en cualquier momento: botón «Omitir tutorial» o Escape.
 *  · Teclado: → / Intro avanza, ← retrocede.
 *  · Es un diálogo modal accesible: el foco se queda en la tarjeta y vuelve a
 *    donde estaba al cerrar.
 *  · Sigue a la zona si la ventana cambia de tamaño o la página se desplaza.
 *  · Con «reducir movimiento» no anima.
 *
 * El resto de la aplicación queda bloqueado mientras dura: se señala, no se
 * toca.
 */
const MARGEN = 12;
const HUECO = 14;
const RELLENO = 6;

interface Caja {
  x: number;
  y: number;
  w: number;
  h: number;
}

function medir(zona: string | undefined): Caja | null {
  if (!zona) return null;
  const el = document.querySelector<HTMLElement>(`[data-tour="${zona}"]`);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width === 0 || r.height === 0) return null;
  return { x: r.left, y: r.top, w: r.width, h: r.height };
}

/** Dónde poner la tarjeta: el lado preferido si cabe y, si no, el primero que quepa. */
function colocar(
  caja: Caja | null,
  tarjeta: { w: number; h: number },
  lado: Paso['lado'],
): { left: number; top: number } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const acotar = (left: number, top: number) => ({
    left: Math.min(Math.max(left, MARGEN), Math.max(MARGEN, vw - tarjeta.w - MARGEN)),
    top: Math.min(Math.max(top, MARGEN), Math.max(MARGEN, vh - tarjeta.h - MARGEN)),
  });

  if (!caja) return acotar((vw - tarjeta.w) / 2, (vh - tarjeta.h) / 2);

  const cx = caja.x + caja.w / 2;
  const cy = caja.y + caja.h / 2;
  const opciones: Record<
    NonNullable<Paso['lado']>,
    { left: number; top: number; cabe: boolean }
  > = {
    derecha: {
      left: caja.x + caja.w + HUECO,
      top: cy - tarjeta.h / 2,
      cabe: caja.x + caja.w + HUECO + tarjeta.w + MARGEN <= vw,
    },
    izquierda: {
      left: caja.x - HUECO - tarjeta.w,
      top: cy - tarjeta.h / 2,
      cabe: caja.x - HUECO - tarjeta.w >= MARGEN,
    },
    abajo: {
      left: cx - tarjeta.w / 2,
      top: caja.y + caja.h + HUECO,
      cabe: caja.y + caja.h + HUECO + tarjeta.h + MARGEN <= vh,
    },
    arriba: {
      left: cx - tarjeta.w / 2,
      top: caja.y - HUECO - tarjeta.h,
      cabe: caja.y - HUECO - tarjeta.h >= MARGEN,
    },
  };

  const orden = [lado, 'abajo', 'arriba', 'derecha', 'izquierda'].filter(
    (l): l is NonNullable<Paso['lado']> => !!l,
  );
  const elegida = orden.find((l) => opciones[l].cabe) ?? orden[0]!;
  return acotar(opciones[elegida].left, opciones[elegida].top);
}

export function Tutorial({ esMovil, onCerrar }: { esMovil: boolean; onCerrar: () => void }) {
  const idMascara = useId();
  const idTitulo = useId();
  const idTexto = useId();

  const [pasos, setPasos] = useState<Paso[]>([]);
  const [indice, setIndice] = useState(0);
  const [caja, setCaja] = useState<Caja | null>(null);
  const [tamano, setTamano] = useState({ w: 352, h: 190 });
  const tarjeta = useRef<HTMLDivElement>(null);
  const siguienteRef = useRef<HTMLButtonElement>(null);
  const focoPrevio = useRef<Element | null>(null);

  // Los pasos se calculan al abrir, ya con la pantalla pintada.
  useLayoutEffect(() => {
    focoPrevio.current = document.activeElement;
    setPasos(pasosAplicables(esMovil));
    return () => {
      if (focoPrevio.current instanceof HTMLElement) focoPrevio.current.focus();
    };
  }, [esMovil]);

  const paso = pasos[indice];
  const ultimo = indice === pasos.length - 1;

  const siguiente = useCallback(() => {
    if (indice >= pasos.length - 1) onCerrar();
    else setIndice((i) => i + 1);
  }, [indice, pasos.length, onCerrar]);
  const anterior = useCallback(() => setIndice((i) => Math.max(0, i - 1)), []);

  // Seguir a la zona: al cambiar de paso, al redimensionar y al desplazar.
  useLayoutEffect(() => {
    if (!paso) return;
    const el = paso.zona ? document.querySelector<HTMLElement>(`[data-tour="${paso.zona}"]`) : null;
    el?.scrollIntoView({ block: 'nearest', inline: 'nearest' });

    const actualizar = () => setCaja(medir(paso.zona));
    actualizar();
    window.addEventListener('resize', actualizar);
    window.addEventListener('scroll', actualizar, true);
    // Las barras laterales se animan al plegarse: se vuelve a medir un instante.
    const temporizador = window.setInterval(actualizar, 300);
    return () => {
      window.removeEventListener('resize', actualizar);
      window.removeEventListener('scroll', actualizar, true);
      window.clearInterval(temporizador);
    };
  }, [paso]);

  // El tamaño real de la tarjeta, para colocarla sin que se salga de la pantalla.
  useLayoutEffect(() => {
    if (!tarjeta.current) return;
    const r = tarjeta.current.getBoundingClientRect();
    setTamano((t) => (t.w === r.width && t.h === r.height ? t : { w: r.width, h: r.height }));
  }, [indice, pasos.length]);

  useEffect(() => {
    siguienteRef.current?.focus();
  }, [indice, pasos.length]);

  useEffect(() => {
    const teclas = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCerrar();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        siguiente();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        anterior();
      } else if (e.key === 'Tab') {
        // Foco atrapado en la tarjeta: tres botones, y de ahí no se sale.
        const botones = tarjeta.current?.querySelectorAll<HTMLElement>('button');
        if (!botones || botones.length === 0) return;
        const primero = botones[0]!;
        const final = botones[botones.length - 1]!;
        if (e.shiftKey && document.activeElement === primero) {
          e.preventDefault();
          final.focus();
        } else if (!e.shiftKey && document.activeElement === final) {
          e.preventDefault();
          primero.focus();
        }
      }
    };
    window.addEventListener('keydown', teclas);
    return () => window.removeEventListener('keydown', teclas);
  }, [siguiente, anterior, onCerrar]);

  if (!paso) return null;

  const hueco = caja && {
    x: caja.x - RELLENO,
    y: caja.y - RELLENO,
    w: caja.w + RELLENO * 2,
    h: caja.h + RELLENO * 2,
  };
  const posicion = colocar(caja, tamano, paso.lado);
  const ancho = Math.min(352, window.innerWidth - MARGEN * 2);

  return createPortal(
    <div className="fixed inset-0 z-[100]">
      {/* Capa que traga los clics: durante el tutorial no se toca la aplicación. */}
      <div className="absolute inset-0" aria-hidden />

      <svg className="pointer-events-none absolute inset-0 size-full" aria-hidden>
        <defs>
          <mask id={idMascara}>
            <rect width="100%" height="100%" fill="white" />
            {hueco && (
              <rect
                x={hueco.x}
                y={hueco.y}
                width={hueco.w}
                height={hueco.h}
                rx="10"
                fill="black"
                style={{ transition: 'all 200ms ease' }}
              />
            )}
          </mask>
        </defs>
        <rect width="100%" height="100%" fill="rgba(3, 7, 18, 0.66)" mask={`url(#${idMascara})`} />
        {hueco && (
          <rect
            x={hueco.x}
            y={hueco.y}
            width={hueco.w}
            height={hueco.h}
            rx="10"
            fill="none"
            stroke="var(--brand)"
            strokeWidth="2"
            style={{ transition: 'all 200ms ease' }}
          />
        )}
      </svg>

      <div
        ref={tarjeta}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        aria-describedby={idTexto}
        className="absolute rounded-xl border border-border bg-surface p-4 shadow-floating"
        style={{ left: posicion.left, top: posicion.top, width: ancho }}
      >
        <p className="tabular text-xs font-medium text-fg-subtle" aria-live="polite">
          Paso {indice + 1} de {pasos.length}
        </p>
        <h2 id={idTitulo} className="mt-1 text-base font-semibold text-fg">
          {paso.titulo}
        </h2>
        <p id={idTexto} className="mt-1.5 text-sm leading-relaxed text-fg-muted">
          {paso.texto}
        </p>

        <div className="mt-4 flex items-center justify-between gap-2">
          {ultimo ? (
            <span />
          ) : (
            <Button variant="ghost" size="sm" onClick={onCerrar}>
              Omitir tutorial
            </Button>
          )}
          <div className="flex gap-2">
            {indice > 0 && (
              <Button size="sm" onClick={anterior}>
                Anterior
              </Button>
            )}
            <Button ref={siguienteRef} variant="primary" size="sm" onClick={siguiente}>
              {ultimo ? 'Terminar' : indice === 0 ? 'Empezar' : 'Siguiente'}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
